// The search a smart folder keeps, run over the wire shapes — so the hub
// (no MaterialItem, no React) and the web shelf agree on who is in it. The
// shelf's own `matches()` works on its rows; a parity spec holds the two
// to the same answer on the fixtures.
//
// Live's rule: every word must match (AND), `#word` is a tag prefix, chips
// of one family OR together (loop / one-shot / MIDI), the rest AND.
//
// Suggestions (tag rows the browser wrote, not yet confirmed) never filter
// unless the search asks: the `suggested` chip ("Suggested too") lets a
// `#word` or a tag chip match a row through a suggestion, and the
// `unreviewed` chip ("Needs review") keeps only rows that still have one.
// A saved search without either is stable — it does not move when the
// browser guesses at the next upload.

import type { MyClip, MyMidi, MySmartFolder } from "./api-types.js";
import { tagAnswersTo, tagsForName, tagsSatisfy } from "./library-tags.js";

export interface LibraryQuery {
  words: string[];
  tags: string[];
}

/** What the search box means: words and `#tags`, lower-case. */
export function parseQuery(text: string): LibraryQuery {
  const words: string[] = [];
  const tags: string[] = [];
  for (const raw of text.trim().toLowerCase().split(/\s+/)) {
    if (!raw) continue;
    if (raw.startsWith("#")) {
      const t = raw.slice(1);
      if (t) tags.push(t);
    } else words.push(raw);
  }
  return { words, tags };
}

export type LibraryItemKind = "loop" | "oneshot" | "midi";

/** One library row as the search sees it: name, kind, every tag it answers to. */
export interface SearchableRow {
  id: string;
  kind: LibraryItemKind;
  name: string;
  tags: string[];
  /** Suggested, not yet confirmed — and not already among `tags`. */
  suggested: string[];
  starred: boolean;
  /** Saved seeds that use the row (clips); 0 for MIDI. */
  uses: number;
  /** Live's Collections colour, 0..6, or null. */
  color: number | null;
}

/** The two chips that let suggestions into a search. */
export const SUGGESTED_CHIP = "suggested";
export const UNREVIEWED_CHIP = "unreviewed";
/** Rows no saved seed uses (library_clip_uses): what a producer prunes. */
export const UNUSED_CHIP = "unused";

/** The suggestions of a row, lower-case, deduplicated, with none that is already a confirmed or derived tag. */
export function suggestedTags(stored: ReadonlyArray<{ tag: string; confirmed: boolean }>, tags: readonly string[]): string[] {
  const have = new Set(tags);
  const out: string[] = [];
  for (const t of stored) {
    const k = t.tag.trim().toLowerCase();
    if (!t.confirmed && k && !have.has(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

/** The tags a row answers to: its kind, its confirmed tags, the family words its name gives. */
export function searchTags(kind: LibraryItemKind, name: string, confirmed: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of [kind, ...confirmed, ...tagsForName(name)]) {
    const k = t.trim().toLowerCase();
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(k);
    }
  }
  return out;
}

export function rowOfClip(c: MyClip): SearchableRow {
  const kind: LibraryItemKind = c.kind === "loop" ? "loop" : "oneshot";
  const tags = searchTags(kind, c.name, (c.tags ?? []).filter((t) => t.confirmed).map((t) => t.tag));
  return { id: c.id, kind, name: c.name, tags, suggested: suggestedTags(c.tags ?? [], tags), starred: c.starred, uses: c.uses ?? 0, color: c.color ?? null };
}

export function rowOfMidi(m: MyMidi): SearchableRow {
  const tags = searchTags("midi", m.name, (m.tags ?? []).filter((t) => t.confirmed).map((t) => t.tag));
  return { id: m.id, kind: "midi", name: m.name, tags, suggested: suggestedTags(m.tags ?? [], tags), starred: m.starred, uses: 0, color: m.color ?? null };
}

/** Whether a row answers a query and a set of chips. */
export function rowMatches(row: SearchableRow, query: LibraryQuery, chips: readonly string[]): boolean {
  const hay = row.name.toLowerCase();
  for (const w of query.words) if (!hay.includes(w)) return false;
  const kinds = new Set<string>();
  const tags: string[] = [];
  let favorite = false;
  let suggested = false;
  let unreviewed = false;
  let unused = false;
  const colors = new Set<number>();
  for (const chip of chips) {
    if (chip === "favorite") favorite = true;
    else if (chip === SUGGESTED_CHIP) suggested = true;
    else if (chip === UNREVIEWED_CHIP) unreviewed = true;
    else if (chip === UNUSED_CHIP) unused = true;
    else if (chip.startsWith("color:")) colors.add(Number(chip.slice(6)));
    else if (chip.startsWith("tag:")) tags.push(chip.slice(4));
    else if (chip === "loop" || chip === "oneshot" || chip === "midi") kinds.add(chip);
    // `track:` chips belong to the room's Project place; a smart folder never carries one.
  }
  // What a `#word` and a tag chip can reach: the row's tags, and its
  // suggestions when the search said so.
  const reach = suggested && row.suggested.length > 0 ? [...row.tags, ...row.suggested] : row.tags;
  for (const t of query.tags) if (!reach.some((tag) => tagAnswersTo(tag, t))) return false;
  if (kinds.size > 0 && !kinds.has(row.kind)) return false;
  if (!tagsSatisfy(reach, tags)) return false;
  if (favorite && !row.starred) return false;
  if (unreviewed && row.suggested.length === 0) return false;
  if (unused && (row.kind === "midi" || row.uses > 0)) return false;
  if (colors.size > 0 && (row.color === null || !colors.has(row.color))) return false;
  return true;
}

/** Who is in a smart folder right now. */
export function smartFolderMembers(sf: MySmartFolder, clips: readonly MyClip[], midi: readonly MyMidi[]): { clipIds: string[]; midiIds: string[] } {
  const query = parseQuery(sf.query);
  return {
    clipIds: clips.filter((c) => rowMatches(rowOfClip(c), query, sf.chips)).map((c) => c.id),
    midiIds: midi.filter((m) => rowMatches(rowOfMidi(m), query, sf.chips)).map((m) => m.id),
  };
}
